import React from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useDispatch, useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { useUpdateClientFeatureMutation } from "src/ui/containers/clients/api";
import { useCurrentWorkspaceClient, useCurrentWorkspaceClientFeatures } from "src/ui/containers/clients/api/helpers";

import { getStatusIndicatorData } from "@dashboard/shared/subscription/subscriptionStatus";
import { closeDetails, selectClientDetails, selectPaneTab, setPaneTab } from "@devices/store";
import { TabContext, TabList } from "@mui/lab";
import { Box, Typography, useTheme } from "@mui/material";
import { SecondaryButton } from "@shared/CustomButton";
import { LoadingBox } from "@shared/Loading";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";

import { PaneElement } from "../shared/PaneElement";
import { PaneTabPanel } from "../shared/PaneTabPanel";
import { useStyle } from "../shared/style";
import { Tab } from "../shared/Tab";

import LimitsTab from "./LimitsTab/LimitsTab";
import WorkspaceTab from "./WorkspaceTab/WorkspaceTab";

const clientTabs = ["WORKSPACE", "FEATURES"];

export const ClientSidePanel = () => {
    const { t } = useTranslation(["common", "sidepanels", "modals", "pages"]);
    const dispatch = useDispatch();
    const theme = useTheme();
    const { classes } = useStyle();
    const currentWorkspaceId = useSelector(selectCurrentWorkspaceId);
    const details = useSelector(selectClientDetails);
    const value = useSelector(selectPaneTab);
    const isClientTab = clientTabs.includes(value);

    const { data: selectedClient, isLoading: loading, isError, refetch } = useCurrentWorkspaceClient(details?.selectedId);
    const { data: clientFeatures, isLoading: featuresLoading } = useCurrentWorkspaceClientFeatures(details?.selectedId);
    const [updateClientFeature] = useUpdateClientFeatureMutation();

    const onChange = (_event: React.ChangeEvent<object>, tabTitle: (typeof clientTabs)[number]) => {
        dispatch(setPaneTab(tabTitle));
    };

    const handleAddonToggle = async (limitType: string, newValue: number) => {
        if (!currentWorkspaceId || !details?.selectedId) {
            return;
        }

        try {
            await updateClientFeature({
                workspaceId: currentWorkspaceId,
                clientId: details.selectedId,
                type: limitType,
                value: newValue,
            }).unwrap();

            showSuccessToast(t("modals:changeLimits.toast.addonUpdatedSuccess"));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("modals:changeLimits.toast.addonUpdateFailed"));
        }
    };

    React.useEffect(() => {
        if (!isClientTab) {
            dispatch(setPaneTab("WORKSPACE"));
        }
    }, [isClientTab, dispatch]);

    if (!isClientTab) {
        return null;
    }

    if (isError && !selectedClient) {
        return (
            <React.Fragment>
                <PaneElement.HeaderBox>
                    <Typography variant="h6" className={classes.panelTitle}>
                        {t("common:label.details")}
                    </Typography>
                    <Box className={classes.buttonBox}>
                        <SecondaryButton onClick={() => dispatch(closeDetails())}>
                            <i className="fas fa-times" />
                        </SecondaryButton>
                    </Box>
                </PaneElement.HeaderBox>
                <PaneElement.Container>
                    <Box className={classes.fetchErrorBox}>
                        <Typography>{t("pages:clientsOverviewPage.toast.fetchClientError")}</Typography>
                        <SecondaryButton onClick={() => refetch()}>
                            {t("common:error.technicalDifficulties.refreshButton")}
                        </SecondaryButton>
                    </Box>
                </PaneElement.Container>
            </React.Fragment>
        );
    }
    if (!selectedClient || loading || featuresLoading) {
        return <LoadingBox className={classes.loadingBox} />;
    }

    const statusIndicator = selectedClient?.subscription?.status
        ? getStatusIndicatorData(selectedClient.subscription.status, theme)
        : {
              tooltip: i18next.t("pages:clientsOverviewPage.table.tooltip.unknownStatus"),
              color: theme.palette.emteria.text.secondary,
          };

    return (
        <TabContext value={value}>
            <PaneElement.HeaderBox>
                <Box className={classes.headerBox}>
                    <Box className={classes.licenseSummaryBox}>
                        <Tooltip title={statusIndicator.tooltip}>
                            <Box className={classes.licenseSummaryIndicator} style={{ backgroundColor: statusIndicator.color }} />
                        </Tooltip>
                        <Typography variant="h6" className={classes.panelTitle}>
                            {selectedClient?.organizationName || t("common:label.details")}
                        </Typography>
                    </Box>
                </Box>

                <Box className={classes.buttonBox}>
                    <SecondaryButton onClick={() => dispatch(closeDetails())}>
                        <i className="fa-solid fa-xmark" />
                    </SecondaryButton>
                </Box>
            </PaneElement.HeaderBox>

            <TabList onChange={onChange} variant="scrollable" scrollButtons>
                <Tab
                    value="WORKSPACE"
                    label={t("sidepanels:clientsPanel.workspaceTab.title")}
                    icon={<i className="fa-solid fa-gauge-high" />}
                />
                <Tab value="FEATURES" label={t("common:limits.subscriptionLimits")} icon={<i className="fa-solid fa-file" />} />
            </TabList>

            <Box className={classes.contentBox}>
                <PaneTabPanel value="WORKSPACE">
                    <WorkspaceTab workspace={selectedClient} />
                </PaneTabPanel>

                <PaneTabPanel value="FEATURES">
                    <LimitsTab
                        clientFeatures={clientFeatures ?? null}
                        clientId={details?.selectedId}
                        onAddonToggle={handleAddonToggle}
                    />
                </PaneTabPanel>
            </Box>
        </TabContext>
    );
};
